import { EMPTY_PERIOD_FILTER, PeriodFilterValue } from '@/common/periodFilter';
import { useGetTrainingStudentFilters } from '@/hooks/useGetTrainingStudentFilters';
import { useLocale } from '@/providers/LocaleProvider';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectMenuOption, BaseSelectorOption } from '@/types/components/selector.types';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Button } from './Button';
import { PeriodFilter } from './PeriodFilter';
import { SelectMenu } from './SelectMenu';
import { MultipleFilter } from './selectors/MultipleFilter';

export interface TrainingStudentFilters {
  provider: BaseSelectMenuOption | null;
  faculties: BaseSelectorOption[];
  courses: BaseSelectorOption[];
  enrolmentDate: PeriodFilterValue;
  completedDate: PeriodFilterValue;
  dueDate: PeriodFilterValue;
  expiryDate: PeriodFilterValue;
}

// No provider selected means every provider, matching the Courses tab. Pre-selecting OLC here would
// filter the landing view to the catalog without the user asking, and would show nothing at all to an
// org whose training is all internal.
export const EMPTY_TRAINING_STUDENT_FILTERS: TrainingStudentFilters = {
  provider: null,
  faculties: [],
  courses: [],
  enrolmentDate: EMPTY_PERIOD_FILTER,
  completedDate: EMPTY_PERIOD_FILTER,
  dueDate: EMPTY_PERIOD_FILTER,
  expiryDate: EMPTY_PERIOD_FILTER,
};

interface TrainingStudentFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: TrainingStudentFilters) => void;
  filters: TrainingStudentFilters;
  providerOptions: [string, BaseSelectMenuOption][];
}

export const TrainingStudentFilterDrawer = ({
  open,
  onClose,
  onApply,
  filters,
  providerOptions,
}: TrainingStudentFilterDrawerProps) => {
  const { translate } = useLocale();
  const [localFilters, setLocalFilters] = useState<TrainingStudentFilters>(filters);

  const { data } = useGetTrainingStudentFilters(localFilters.provider?.value as string | undefined);

  const facultyOptions = useMemo<BaseSelectorOption[]>(
    () => (data?.faculties ?? []).map((f) => ({ id: f.id, label: f.name, name: f.name })),
    [data]
  );

  const courseOptions = useMemo<BaseSelectorOption[]>(
    () => (data?.courses ?? []).map((c) => ({ id: c.id, label: c.name, name: c.name })),
    [data]
  );

  useEffect(() => {
    if (
      providerOptions.length > 0 &&
      localFilters.provider?.value &&
      !localFilters.provider.label
    ) {
      const found = providerOptions.find(([, opt]) => opt.value === localFilters.provider!.value);
      if (found) setLocalFilters((prev) => ({ ...prev, provider: found[1] }));
    }
  }, [providerOptions, localFilters.provider]);

  const handleOpen = () => {
    setLocalFilters(filters);
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleCancel = () => {
    setLocalFilters(filters);
    onClose();
  };

  return (
    <Drawer
      open={open}
      anchor='right'
      onClose={handleCancel}
      SlideProps={{ onEnter: handleOpen }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: 480 },
          maxWidth: '100vw',
        },
      }}
    >
      <Stack direction='column' sx={{ height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${KLAYO_COLORS.GreyGR200}`,
          }}
        >
          <Typography variant='h6' fontWeight={600}>
            {translate('manage.filters')}
          </Typography>
          <IconButton size='small' onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Stack spacing={3} sx={{ px: 3, py: 3, flexGrow: 1 }}>
          {/* Provider */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={500}>
              {translate('manage.provider')}
            </Typography>
            <SelectMenu
              items={providerOptions}
              value={localFilters.provider}
              clearable
              dense
              placeholder={translate('manage.selectProvider')}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  provider: e.target.value as BaseSelectMenuOption | null,
                  faculties: [],
                  courses: [],
                }))
              }
              sx={{ width: '100%' }}
            />
          </Stack>

          {/* Faculty */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={500}>
              {translate('manage.faculty')}
            </Typography>
            <MultipleFilter
              options={facultyOptions}
              value={localFilters.faculties}
              placeholder={translate('manage.selectFaculty')}
              getItemLabel={(option) => option.label ?? option.name ?? ''}
              onChange={(_e, selected) =>
                setLocalFilters((prev) => ({ ...prev, faculties: selected }))
              }
            />
          </Stack>

          {/* Course */}
          <Stack spacing={1}>
            <Typography variant='body2' fontWeight={500}>
              {translate('manage.course')}
            </Typography>
            <MultipleFilter
              options={courseOptions}
              value={localFilters.courses}
              placeholder={translate('manage.selectCourse')}
              getItemLabel={(option) => option.label ?? option.name ?? ''}
              onChange={(_e, selected) =>
                setLocalFilters((prev) => ({ ...prev, courses: selected }))
              }
            />
          </Stack>

          {/* Enrolment date (past) */}
          <PeriodFilter
            label={translate('manage.enrolmentDate')}
            direction='past'
            value={localFilters.enrolmentDate}
            onChange={(value) => setLocalFilters((prev) => ({ ...prev, enrolmentDate: value }))}
          />

          {/* Completed date (past) */}
          <PeriodFilter
            label={translate('manage.completedDate')}
            direction='past'
            value={localFilters.completedDate}
            onChange={(value) => setLocalFilters((prev) => ({ ...prev, completedDate: value }))}
          />

          {/* Due date (future) */}
          <PeriodFilter
            label={translate('manage.dueDate')}
            direction='future'
            value={localFilters.dueDate}
            onChange={(value) => setLocalFilters((prev) => ({ ...prev, dueDate: value }))}
          />

          {/* Expiry date (future) */}
          <PeriodFilter
            label={translate('manage.expiryDate')}
            direction='future'
            value={localFilters.expiryDate}
            onChange={(value) => setLocalFilters((prev) => ({ ...prev, expiryDate: value }))}
          />
        </Stack>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${KLAYO_COLORS.GreyGR200}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            variant='outlined'
            label={translate('common.cancel')}
            size='md'
            onClick={handleCancel}
          />
          <Button
            variant='filled'
            label={translate('common.apply')}
            size='md'
            onClick={handleApply}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};
